import * as React from 'react';
import {
  AddTile,
  BottomSheet,
  BottomSheetContent,
  Button,
  ConfirmDialog,
  Form,
  FormField,
  Input,
  OptionSheet,
  Stack,
  applyServerErrors,
  useZodForm,
} from '@relay-towing/ui';
import { Trash } from '@phosphor-icons/react';
import { useGetVehicleMakes } from '../../api/hooks/useGetVehicleMakes';
import { useGetVehicleModels } from '../../api/hooks/useGetVehicleModels';
import { useUpdateVehicle } from '../../hooks/useUpdateVehicle';
import type { ScannedVin } from '../../hooks/useScanVin';
import { useVinDecoder } from '../../hooks/useVinDecoder';
import {
  CLEARED_VEHICLE,
  toVehicleUpdate,
  vehicleSchema,
  vehicleValuesOf,
  type VehicleRecord,
  type VehicleValues,
} from '../../lib/vehicleSchema';
import { VinScanSheet } from './VinScanSheet';

/** Next year's models back to 1950, newest first, as the Year picker lists them. */
const YEARS = Array.from(
  { length: new Date().getFullYear() + 2 - 1950 },
  (_, index) => String(new Date().getFullYear() + 1 - index)
).map((year) => ({ value: year, label: year }));

/** A VIN that can be decoded: 17 characters, never I, O or Q. */
const DECODABLE = /^[A-HJ-NPR-Z0-9]{17}$/;

/**
 * The option that spells a decoded word, whatever its case: the decoder
 * says TESLA where the makes list says Tesla, and the field should show the
 * list's.
 */
const matching = (options: { value: string }[] | undefined, word: string) =>
  options?.find((option) => option.value.toLowerCase() === word.toLowerCase())
    ?.value ?? word;

/**
 * A vehicle's details on a phone, as the Figma's Add Vehicle and Vehicle
 * sheets draw them (865:32350, 865:32488): the VIN with Scan VIN under it,
 * then Make, Model and Year picked from lists, Color, ODO and Plate, with
 * Cancel (or Back, to where it was opened from) and the save along the
 * foot. A VIN typed out in full is decoded and fills whichever of make,
 * model and year are still empty; a scanned one fills them from what it
 * decodes to. With `deletable`, Delete Vehicle sits above the foot
 * (865:32575) and, once confirmed, clears every field.
 *
 * Saved through PUT /calls/vehicle/:vehicleId, which the server lets only a
 * member who may change the call, or once it is impounded the impound,
 * use. The call's and the impound's vehicle are the same record and draw
 * this one sheet.
 */
export function VehicleSheet({
  open,
  onOpenChange,
  vehicle,
  title = 'Vehicle',
  submitLabel = 'Update',
  seed,
  typingVin = false,
  onBack,
  deletable = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vehicle: VehicleRecord;
  /** "Add Vehicle" for an empty one, "Vehicle" to change one. */
  title?: string;
  submitLabel?: string;
  /** What a scan already found, laid over the vehicle's own values. */
  seed?: Partial<VehicleValues>;
  /** Opens with the VIN box focused, from "Enter manually" or "Tap to edit". */
  typingVin?: boolean;
  /** A Back in place of Cancel, to the sheet this was opened from. */
  onBack?: () => void;
  deletable?: boolean;
}) {
  const formId = React.useId();
  const updateVehicle = useUpdateVehicle();
  const [confirming, setConfirming] = React.useState(false);
  const save = (updateData: typeof CLEARED_VEHICLE) =>
    updateVehicle.mutateAsync({ vehicleId: vehicle.id, updateData });

  return (
    <BottomSheet open={open} onOpenChange={onOpenChange}>
      <BottomSheetContent
        title={title}
        size="full"
        footer={
          <>
            <Button
              variant="secondary"
              size="xl"
              pill
              disabled={updateVehicle.isPending}
              onClick={onBack ?? (() => onOpenChange(false))}
            >
              {onBack ? 'Back' : 'Cancel'}
            </Button>
            <Button
              type="submit"
              form={formId}
              size="xl"
              pill
              loading={updateVehicle.isPending && !confirming}
            >
              {submitLabel}
            </Button>
          </>
        }
      >
        <VehicleForm
          id={formId}
          vehicle={vehicle}
          seed={seed}
          typingVin={typingVin}
          onSave={async (values) => {
            await save(toVehicleUpdate(values));
            onOpenChange(false);
          }}
          onDelete={deletable ? () => setConfirming(true) : undefined}
        />
        <ConfirmDialog
          open={confirming}
          onOpenChange={setConfirming}
          title="Delete this vehicle?"
          description="Its VIN, make, model, year, color, miles and plate are cleared from the call. Its photos and notes stay."
          confirmLabel="Delete Vehicle"
          destructive
          loading={updateVehicle.isPending}
          onConfirm={() => {
            // A failed delete leaves the sheet open, to try again or keep.
            void save(CLEARED_VEHICLE)
              .then(
                () => onOpenChange(false),
                () => undefined
              )
              .finally(() => setConfirming(false));
          }}
        />
      </BottomSheetContent>
    </BottomSheet>
  );
}

/**
 * The sheet's fields. Its own component so the form is made each time the
 * sheet opens, from the vehicle as it is then: the sheet's content is
 * unmounted while it is shut.
 */
function VehicleForm({
  id,
  vehicle,
  seed,
  typingVin,
  onSave,
  onDelete,
}: {
  id: string;
  vehicle: VehicleRecord;
  seed?: Partial<VehicleValues>;
  typingVin: boolean;
  onSave: (values: VehicleValues) => Promise<void>;
  onDelete?: () => void;
}) {
  const { decodeVin } = useVinDecoder();
  const { data: makes } = useGetVehicleMakes();
  const [scanning, setScanning] = React.useState(false);
  const form = useZodForm(vehicleSchema, {
    defaultValues: { ...vehicleValuesOf(vehicle), ...seed },
  });
  const make = form.watch('make');
  const { data: models } = useGetVehicleModels({ make }, { enabled: !!make });
  const makeOptions = React.useMemo(
    () =>
      (makes ?? []).map((entry) => ({ value: entry.make, label: entry.make })),
    [makes]
  );
  const modelOptions = (models ?? []).map((entry) => ({
    value: entry.model,
    label: entry.model,
  }));

  React.useEffect(() => {
    if (typingVin) form.setFocus('vin');
  }, [typingVin, form]);

  // A make that came from the decoder, in its capitals, takes the list's
  // spelling once the list is here.
  React.useEffect(() => {
    const current = form.getValues('make');
    const listed = matching(makeOptions, current);
    if (listed !== current) form.setValue('make', listed);
  }, [makeOptions, form]);

  /** `over` replaces what is there: a scan the member asked for. */
  const fill = (
    decoded: { make?: string; model?: string; year?: number },
    over = false
  ) => {
    const options = { shouldValidate: form.formState.isSubmitted };
    const put = (name: 'make' | 'model' | 'year', value: string) => {
      if (over || !form.getValues(name)) form.setValue(name, value, options);
    };
    if (decoded.make) put('make', matching(makeOptions, decoded.make));
    if (decoded.model) put('model', decoded.model);
    if (decoded.year) put('year', String(decoded.year));
  };

  const decode = async (vin: string) => {
    if (!DECODABLE.test(vin.trim().toUpperCase())) return;
    const decoded = await decodeVin(vin.trim().toUpperCase());
    if (decoded) fill(decoded);
  };

  const scanned = ({ vin, vehicle: decoded }: ScannedVin) => {
    setScanning(false);
    form.setValue('vin', vin, { shouldValidate: form.formState.isSubmitted });
    if (decoded) fill(decoded, true);
  };

  const save = async (values: VehicleValues) => {
    try {
      await onSave(values);
    } catch (error) {
      applyServerErrors(form, error, 'The vehicle could not be saved.');
    }
  };

  // The VIN and its Scan VIN tile, then the rest, 40 apart as the Figma's
  // two groups are; 16 between fields within each.
  return (
    <Form form={form} id={id} gap={10} onSubmit={save}>
      <Stack gap={4}>
        <FormField form={form} name="vin" label="VIN">
          {(field) => (
            <Input
              {...field}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              maxLength={17}
              placeholder="17 characters"
              onChange={(event) => {
                field.onChange(event);
                void decode(event.target.value);
              }}
            />
          )}
        </FormField>
        <AddTile width="block" compact onClick={() => setScanning(true)}>
          Scan VIN
        </AddTile>
        <VinScanSheet
          open={scanning}
          onOpenChange={setScanning}
          onRead={scanned}
          onEnterManually={(vin) => {
            setScanning(false);
            if (vin) form.setValue('vin', vin);
            form.setFocus('vin');
          }}
        />
      </Stack>
      <Stack gap={4}>
        <FormField form={form} name="make" label="Make">
          {(field) => (
            <OptionSheet
              title="Make"
              searchable
              allowCustom
              placeholder="Select"
              options={makeOptions}
              value={field.value || undefined}
              onValueChange={(value) => {
                field.onChange(value);
                form.setValue('model', '');
              }}
            />
          )}
        </FormField>
        <FormField form={form} name="model" label="Model">
          {(field) => (
            <OptionSheet
              title="Model"
              searchable
              allowCustom
              placeholder="Select"
              options={modelOptions}
              value={field.value || undefined}
              onValueChange={field.onChange}
            />
          )}
        </FormField>
        <FormField form={form} name="year" label="Year">
          {(field) => (
            <OptionSheet
              title="Year"
              searchable
              placeholder="Select"
              options={YEARS}
              value={field.value || undefined}
              onValueChange={field.onChange}
            />
          )}
        </FormField>
        <FormField form={form} name="color" label="Color">
          {(field) => <Input {...field} placeholder="Paint color" />}
        </FormField>
        <FormField form={form} name="odometer" label="ODO">
          {(field) => (
            <Input {...field} inputMode="numeric" placeholder="Miles" />
          )}
        </FormField>
        <FormField form={form} name="licensePlate" label="Plate">
          {(field) => (
            <Input
              {...field}
              autoCapitalize="characters"
              autoCorrect="off"
              placeholder="Plate number"
            />
          )}
        </FormField>
      </Stack>
      {onDelete ? (
        <Button
          variant="destructive-secondary"
          size="xl"
          pill
          fullWidth
          leadingIcon={<Trash />}
          onClick={onDelete}
        >
          Delete Vehicle
        </Button>
      ) : null}
    </Form>
  );
}
